import { Card, Flex, Space, Input, Button, notification, Form, Typography, DatePicker, Drawer, List, Tag, Spin, AutoComplete, Alert, Select } from "antd"
const { RangePicker } = DatePicker;
import { useForm } from "antd/es/form/Form"
import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { postData } from "../../../actions/developer"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import { FileExcelOutlined, HistoryOutlined, LockOutlined, CheckCircleOutlined, EditOutlined } from "@ant-design/icons"
import axios from "axios"
import moment from "moment"
import RenderFormItem from "../../../components/Forms/FormItems"
import { FormColumnConfig } from "../../../interfaces/entity"

const { Title, Text } = Typography

const TimesheetForm = () => {
    const [step, setStep] = useState<1 | 2 | 3>(1); // 1: Email, 2: OTP, 3: Form
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [email, setEmail] = useState<string>("");
    const [form] = useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [timesheets, setTimesheets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [projectNames, setProjectNames] = useState<string[]>([]);
    const [projectNamesLoading, setProjectNamesLoading] = useState(false);
    const [isWeekendSelected, setIsWeekendSelected] = useState(false);
    const [existingTimesheetDate, setExistingTimesheetDate] = useState<string | null>(null);
    const [entryType, setEntryType] = useState<'workday' | 'leave' | 'holiday'>('workday');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Derived states for backward compatibility
    const isLeave = entryType === 'leave';
    const isHoliday = entryType === 'holiday';

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Check if access token is in URL params
        const token = searchParams.get('token');
        if (token) {
            setAccessToken(token);
            setStep(3);
        }
    }, [searchParams]);

    useEffect(() => {
        // Load timesheets when access token is available and sidebar is open
        if (accessToken && sidebarVisible) {
            loadTimesheets();
        }
    }, [accessToken, sidebarVisible]);

    useEffect(() => {
        // Load project names when access token is available and user is on step 3
        if (accessToken && step === 3) {
            loadProjectNames();
        }
    }, [accessToken, step]);

    // Check if initial date (today) is a weekend and if timesheet exists
    useEffect(() => {
        const today = dayjs();
        const dayOfWeek = today.day();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        setIsWeekendSelected(isWeekend);
        // Check if timesheet exists for today
        if (accessToken && step === 3) {
            checkExistingTimesheetForDate(today);
        }
    }, [accessToken, step]);

    const sendOTP = async (values: any) => {
        try {
            const resData = await dispatch(postData('/timesheet/public/send-otp', { email: values.email }));
            if (resData.code == 200) {
                notification.success({
                    message: 'OTP sent successfully',
                    description: 'Please check your email for the OTP',
                    type: 'success'
                });
                setEmail(values.email);
                setStep(2);
            } else {
                notification.error({
                    message: resData.message || 'Failed to send OTP',
                    type: 'error'
                });
            }
        } catch (error) {
            notification.error({
                message: 'Something went wrong. Please try again later.',
                type: 'error'
            });
        }
    };

    const verifyOTP = async (values: any) => {
        try {
            const resData = await dispatch(postData('/timesheet/public/verify-otp', {
                email: email,
                otp: values.otp
            }));
            if (resData.code == 200) {
                notification.success({
                    message: 'OTP verified successfully',
                    type: 'success'
                });
                const token = resData.data.accessToken;
                setAccessToken(token);
                localStorage.setItem('timesheet_access_token', token);
                // Navigate to dashboard after verification
                navigate(`/timesheet/dashboard?token=${token}`);
            } else {
                notification.error({
                    message: resData.message || 'Invalid OTP',
                    type: 'error'
                });
            }
        } catch (error) {
            notification.error({
                message: 'Something went wrong. Please try again later.',
                type: 'error'
            });
        }
    };

    const submitTimesheet = async (values: any) => {
        try {
            // Convert projectName to string if it's an array (from tags mode)
            const projectName = Array.isArray(values.projectName)
                ? values.projectName[0] || values.projectName.join(', ')
                : values.projectName;

            const entryTypeValue = values.entryType || entryType;
            const isLeaveValue = entryTypeValue === 'leave';
            const isHolidayValue = entryTypeValue === 'holiday';

            const payload = {
                accessToken: accessToken,
                projectName: projectName,
                date: values.date.format('YYYY-MM-DD'),
                module: isLeaveValue ? 'Leave' : (isHolidayValue ? 'Holiday' : values.module),
                taskDetails: isLeaveValue ? 'Leave for the day' : (isHolidayValue ? 'Holiday for the day' : values.taskDetails),
                workedHours: isLeaveValue || isHolidayValue ? 0 : values.workedHours,
                isLeave: isLeaveValue,
                isHoliday: isHolidayValue
            };

            const resData = await dispatch(postData('/timesheet/public/submit', payload));
            if (resData.code == 200) {
                notification.success({
                    message: 'Timesheet submitted successfully',
                    type: 'success'
                });
                form.resetFields();
                // Reload timesheets if sidebar is open
                if (sidebarVisible && accessToken) {
                    loadTimesheets();
                }
                // Reload project names to include the newly added project
                if (accessToken) {
                    loadProjectNames();
                }
            } else {
                notification.error({
                    message: resData.message || 'Failed to submit timesheet',
                    type: 'error'
                });
            }
        } catch (error) {
            notification.error({
                message: 'Something went wrong. Please try again later.',
                type: 'error'
            });
        }
    };

    const loadTimesheets = async () => {
        if (!accessToken) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                accessToken: accessToken
            });

            if (dateRange && dateRange[0] && dateRange[1]) {
                params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axios.get(`${process.env.API_URL}/timesheet/public/my-timesheets?${params.toString()}`);
            if (response.data.code === 200) {
                setTimesheets(response.data.data.timesheets || []);
            }
        } catch (error) {
            console.error('Error loading timesheets:', error);
            notification.error({
                message: 'Failed to load timesheets',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!accessToken) return;

        setExportLoading(true);
        try {
            const params = new URLSearchParams({
                accessToken: accessToken
            });

            if (dateRange && dateRange[0] && dateRange[1]) {
                params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axios.get(`${process.env.API_URL}/timesheet/public/export?${params.toString()}`, {
                responseType: 'blob'
            });

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const startDate = dateRange?.[0]?.format('YYYY-MM-DD') || 'all';
            const endDate = dateRange?.[1]?.format('YYYY-MM-DD') || 'all';
            const filename = `timesheet_${startDate}_to_${endDate}_${moment().format('YYYY-MM-DD')}.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            notification.success({
                message: 'Timesheet exported successfully',
                type: 'success'
            });
        } catch (error) {
            console.error('Error exporting timesheet:', error);
            notification.error({
                message: 'Failed to export timesheet',
                type: 'error'
            });
        } finally {
            setExportLoading(false);
        }
    };

    const handleResendOTP = () => {
        sendOTP({ email: email });
    };

    const loadProjectNames = async () => {
        if (!accessToken) return;

        setProjectNamesLoading(true);
        try {
            const params = new URLSearchParams({
                accessToken: accessToken
            });

            const response = await axios.get(`${process.env.API_URL}/timesheet/public/project-names?${params.toString()}`);
            if (response.data.code === 200) {
                // Handle both nested and direct data structure
                const projects = response.data.data?.data || response.data.data || [];
                const names = projects.map((p: any) => p.name || p._id || p).filter(Boolean);
                setProjectNames(names);
            }
        } catch (error) {
            console.error('Error loading project names:', error);
        } finally {
            setProjectNamesLoading(false);
        }
    };

    const checkExistingTimesheetForDate = async (selectedDate: dayjs.Dayjs) => {
        if (!accessToken || !selectedDate) {
            setExistingTimesheetDate(null);
            return;
        }

        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const params = new URLSearchParams({
                accessToken: accessToken,
                startDate: dateStr,
                endDate: dateStr
            });
            const response = await axios.get(`${process.env.API_URL}/timesheet/public/my-timesheets?${params.toString()}`);
            if (response.data.code === 200) {
                const data = response.data.data?.data || response.data.data || [];
                const timesheetData = Array.isArray(data) ? data : (data.timesheets || []);
                if (timesheetData.length > 0) {
                    setExistingTimesheetDate(dateStr);
                } else {
                    setExistingTimesheetDate(null);
                }
            } else {
                setExistingTimesheetDate(null);
            }
        } catch (error) {
            console.error('Error checking existing timesheet:', error);
            setExistingTimesheetDate(null);
        }
    };

    const onFinish = async (values: any) => {
        if (step === 1) {
            await sendOTP(values);
        } else if (step === 2) {
            await verifyOTP(values);
        } else if (step === 3) {
            await submitTimesheet(values);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? '20px 16px' : '40px 20px'
        }}>
            <div style={{ maxWidth: 600, margin: '0 auto', width: '100%' }}>
                {step === 3 && accessToken && (
                    <div style={{ marginBottom: 20, textAlign: 'right' }}>
                        <Button
                            icon={<HistoryOutlined />}
                            onClick={() => setSidebarVisible(true)}
                            type="default"
                            style={{
                                borderRadius: '8px',
                                height: '40px',
                                background: '#fff',
                                borderColor: '#fff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                fontWeight: 500
                            }}
                        >
                            View History
                        </Button>
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Card
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            borderRadius: '16px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                            border: 'none'
                        }}
                        bodyStyle={{ padding: isMobile ? '24px' : '40px' }}
                    >
                        <Flex justify='center' style={{ width: '100%' }}>
                            <Space direction='vertical' style={{ width: '100%' }} size="large">
                                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                    <Title level={2} style={{
                                        margin: 0,
                                        textAlign: 'center',
                                        color: '#1a1a1a',
                                        fontWeight: 600,
                                        fontSize: isMobile ? '22px' : '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px'
                                    }}>
                                        {step === 1 && (
                                            <>
                                                <LockOutlined /> Timesheet Access
                                            </>
                                        )}
                                        {step === 2 && (
                                            <>
                                                <CheckCircleOutlined /> Verify OTP
                                            </>
                                        )}
                                        {step === 3 && (
                                            <>
                                                <EditOutlined /> Submit Timesheet
                                            </>
                                        )}
                                    </Title>
                                    <Text style={{
                                        display: 'block',
                                        textAlign: 'center',
                                        marginTop: 12,
                                        fontSize: '15px',
                                        color: '#595959'
                                    }}>
                                        {step === 1 && 'Enter your Accelgrowth email ID to receive an OTP for verification'}
                                        {step === 2 && 'Enter the OTP sent to your email'}
                                        {step === 3 && 'Fill in your timesheet details'}
                                    </Text>
                                </div>

                                <Form
                                    form={form}
                                    layout='vertical'
                                    onFinish={onFinish}
                                    autoComplete="off"
                                >
                                    <Space direction='vertical' style={{ width: '100%' }} size="middle">
                                        {/* Step 1: Email Input */}
                                        {step === 1 && (
                                            <RenderFormItem
                                                column={{
                                                    name: 'email',
                                                    label: 'Email',
                                                    type: 'email',
                                                    rules: [
                                                        { required: true, message: 'Email is required' },
                                                        { type: 'email', message: 'Please enter a valid email' },
                                                        {
                                                            validator: (_, value) => {
                                                                if (!value) return Promise.resolve();
                                                                const domain = value.split('@')[1]?.toLowerCase();
                                                                if (domain !== 'accelgrowthtech.com' && domain !== 'accelgrowth.in' && domain !== 'accelgrowth.com') {
                                                                    return Promise.reject('Only Accelgrowth email addresses are allowed');
                                                                }
                                                                return Promise.resolve();
                                                            }
                                                        }
                                                    ],
                                                    placeholder: 'yourname@accelgrowthtech.com'
                                                } as FormColumnConfig}
                                                form={form}
                                            />
                                        )}

                                        {/* Step 2: OTP Input */}
                                        {step === 2 && (
                                            <>
                                                <Form.Item
                                                    name={'otp'}
                                                    label={'OTP'}
                                                    rules={[{ required: true, message: 'OTP is required' }]}
                                                >
                                                    <Input.OTP length={4} />
                                                </Form.Item>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Button type="link" onClick={handleResendOTP}>
                                                        Resend OTP
                                                    </Button>
                                                </div>
                                            </>
                                        )}

                                        {/* Step 3: Timesheet Form */}
                                        {step === 3 && (
                                            <>
                                                <Form.Item
                                                    name={'projectName'}
                                                    label={'Project Name'}
                                                    rules={[{ required: true, message: 'Project name is required' }]}
                                                >
                                                    <AutoComplete
                                                        options={projectNames.map(name => ({
                                                            label: name,
                                                            value: name
                                                        }))}
                                                        placeholder={isLeave ? 'Leave' : isHoliday ? 'Holiday' : "Select or type project name"}
                                                        filterOption={(inputValue, option) =>
                                                            (option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase())
                                                        }
                                                        allowClear
                                                        disabled={isLeave || isHoliday}
                                                        notFoundContent={projectNamesLoading ? <Spin size="small" /> : 'No projects found'}
                                                    />
                                                </Form.Item>

                                                <RenderFormItem
                                                    column={{
                                                        name: 'date',
                                                        label: 'Date',
                                                        type: 'date',
                                                        rules: [{ required: true, message: 'Date is required' }],
                                                        initialValue: dayjs(),
                                                        dateProps: {
                                                            format: 'YYYY-MM-DD',
                                                            style: { width: '100%' },
                                                            onChange: (date: any) => {
                                                                if (date) {
                                                                    const dayOfWeek = date.day();
                                                                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
                                                                    setIsWeekendSelected(isWeekend);
                                                                    // Check if timesheet already exists for this date
                                                                    checkExistingTimesheetForDate(date);
                                                                } else {
                                                                    setIsWeekendSelected(false);
                                                                    setExistingTimesheetDate(null);
                                                                }
                                                            }
                                                        }
                                                    } as FormColumnConfig}
                                                    form={form}
                                                />
                                                <Form.Item
                                                    name="entryType"
                                                    label="Entry Type"
                                                    initialValue="workday"
                                                >
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        options={[
                                                            { label: 'Workday', value: 'workday' },
                                                            { label: 'Leave', value: 'leave' },
                                                            { label: 'Holiday', value: 'holiday' }
                                                        ]}
                                                        value={entryType}
                                                        onChange={(value: 'workday' | 'leave' | 'holiday') => {
                                                            setEntryType(value);
                                                            form.setFieldsValue({ entryType: value });

                                                            if (value === 'leave' || value === 'holiday') {
                                                                // Set worked hours to 0 and set default values
                                                                form.setFieldsValue({
                                                                    workedHours: 0,
                                                                    module: value === 'leave' ? 'Leave' : 'Holiday',
                                                                    taskDetails: value === 'leave' ? 'Leave for the day' : 'Holiday for the day',
                                                                    projectName: value === 'leave' ? 'Leave' : 'Holiday'
                                                                });
                                                            } else {
                                                                // Clear fields when switching back to workday
                                                                form.setFieldsValue({
                                                                    module: undefined,
                                                                    taskDetails: undefined,
                                                                    workedHours: undefined,
                                                                    projectName: undefined
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </Form.Item>
                                                {isWeekendSelected && (
                                                    <Alert
                                                        message="Weekend Selected"
                                                        description="The selected day is a weekend. You will be required to submit an approval screenshot for this work."
                                                        type="warning"
                                                        showIcon
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                )}
                                                {isLeave && (
                                                    <Alert
                                                        message="Leave Selected"
                                                        description="You have marked this day as leave. You will be required to submit an approval proof for this leave."
                                                        type="warning"
                                                        showIcon
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                )}
                                                {existingTimesheetDate && (
                                                    <Alert
                                                        message="Timesheet Already Exists"
                                                        description={`A timesheet entry already exists for ${moment(existingTimesheetDate).format('DD MMM YYYY')}. Submitting this form will create a duplicate entry.`}
                                                        type="info"
                                                        showIcon
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                )}

                                                <RenderFormItem
                                                    column={{
                                                        name: 'module',
                                                        label: 'Module',
                                                        type: 'text',
                                                        rules: [{ required: !isLeave && !isHoliday, message: 'Module is required' }],
                                                        placeholder: 'Enter module name',
                                                        inputProps: {
                                                            disabled: isLeave || isHoliday
                                                        }
                                                    } as FormColumnConfig}
                                                    form={form}
                                                />

                                                <RenderFormItem
                                                    column={{
                                                        name: 'taskDetails',
                                                        label: 'Task Details',
                                                        type: 'textarea',
                                                        rules: [{ required: !isLeave && !isHoliday, message: 'Task details are required' }],
                                                        placeholder: 'Describe the task you worked on',
                                                        inputTextAreaProps: {
                                                            rows: 4,
                                                            disabled: isLeave || isHoliday
                                                        }
                                                    } as FormColumnConfig}
                                                    form={form}
                                                />

                                                <RenderFormItem
                                                    column={{
                                                        name: 'workedHours',
                                                        label: 'Worked Hours',
                                                        type: 'number',
                                                        rules: [
                                                            { required: !isLeave && !isHoliday, message: 'Worked hours are required' },
                                                            { type: 'number', min: 0, max: 24, message: 'Hours must be between 0 and 24' }
                                                        ],
                                                        placeholder: 'Enter hours worked',
                                                        inputNumberProps: {
                                                            min: 0,
                                                            max: 24,
                                                            step: 0.5,
                                                            precision: 2,
                                                            style: { width: '100%' },
                                                            disabled: isLeave || isHoliday
                                                        }
                                                    } as FormColumnConfig}
                                                    form={form}
                                                />

                                                <Button
                                                    type='primary'
                                                    style={{
                                                        width: '100%',
                                                        height: '48px',
                                                        borderRadius: '8px',
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        border: 'none',
                                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                                    }}
                                                    htmlType='submit'
                                                    size="large"
                                                >
                                                    {(step as number) === 1 && 'Send OTP'}
                                                    {(step as number) === 2 && 'Verify OTP'}
                                                    {(step as number) === 3 && 'Submit Timesheet'}
                                                </Button>

                                                {(step as number) > 1 && (
                                                    <Button
                                                        type="link"
                                                        style={{ width: '100%' }}
                                                        onClick={() => {
                                                            if ((step as number) === 2) {
                                                                setStep(1);
                                                                form.resetFields();
                                                            } else if ((step as number) === 3) {
                                                                setStep(1);
                                                                setAccessToken(null);
                                                                setEmail("");
                                                                form.resetFields();
                                                            }
                                                        }}
                                                    >
                                                        Back
                                                    </Button>
                                                )}
                                            </>
                                        )}

                                        <Button
                                            type='primary'
                                            style={{
                                                width: '100%',
                                                height: '48px',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                            }}
                                            htmlType='submit'
                                            size="large"
                                        >
                                            {(step as number) === 1 && 'Send OTP'}
                                            {(step as number) === 2 && 'Verify OTP'}
                                            {(step as number) === 3 && 'Submit Timesheet'}
                                        </Button>

                                        {(step as number) > 1 && (
                                            <Button
                                                type="link"
                                                style={{ width: '100%' }}
                                                onClick={() => {
                                                    if ((step as number) === 2) {
                                                        setStep(1);
                                                        form.resetFields();
                                                    } else if ((step as number) === 3) {
                                                        setStep(1);
                                                        setAccessToken(null);
                                                        setEmail("");
                                                        form.resetFields();
                                                    }
                                                }}
                                            >
                                                Back
                                            </Button>
                                        )}
                                    </Space>
                                </Form>
                            </Space>
                        </Flex>
                    </Card>
                </div>
            </div>

            {/* Sidebar for saved entries */}
            <Drawer
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Timesheet History</span>
                        {accessToken && (
                            <Button
                                icon={<FileExcelOutlined />}
                                onClick={handleExport}
                                loading={exportLoading}
                                type="primary"
                                size="small"
                            >
                                Export to Excel
                            </Button>
                        )}
                    </div>
                }
                placement="right"
                onClose={() => setSidebarVisible(false)}
                open={sidebarVisible}
                width={600}
            >
                {accessToken ? (
                    <>
                        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                            <Text strong>Filter by Date Range:</Text>
                            <RangePicker
                                style={{ width: '100%' }}
                                value={dateRange}
                                onChange={(dates) => {
                                    setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
                                }}
                                format="YYYY-MM-DD"
                            />
                            <Space>
                                <Button size="small" onClick={loadTimesheets}>
                                    Apply Filter
                                </Button>
                                <Button size="small" onClick={() => {
                                    setDateRange(null);
                                    loadTimesheets();
                                }}>
                                    Clear
                                </Button>
                            </Space>
                        </Space>

                        <Spin spinning={loading}>
                            {timesheets.length > 0 ? (
                                <List
                                    dataSource={timesheets}
                                    renderItem={(item: any) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text strong>{item.projectName}</Text>
                                                        <Tag color="blue">{item.workedHours} hrs</Tag>
                                                    </div>
                                                }
                                                description={
                                                    <div>
                                                        <div><Text type="secondary">Date: </Text>{moment(item.date).format('DD MMM YYYY')}</div>
                                                        <div><Text type="secondary">Module: </Text>{item.module}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <Text type="secondary">Task: </Text>
                                                            <Text>{item.taskDetails}</Text>
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Text type="secondary">No timesheet entries found</Text>
                                </div>
                            )}
                        </Spin>

                        {timesheets.length > 0 && (
                            <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                                <Text strong>
                                    Total Hours: {timesheets.reduce((sum, t) => sum + (t.workedHours || 0), 0).toFixed(2)} hrs
                                </Text>
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Text type="secondary">Please verify your email to view timesheet history</Text>
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default TimesheetForm;
